// Pure helpers for company seasons UI (unit-testable, no `this`).

// True if any season's current day/month differs from its saved snapshot (orig_*).
// Accepts an array or an object-map of seasons. String comparison so 1 === "1".
export function seasonsDatesChanged(seasons) {
  const list = Array.isArray(seasons) ? seasons : Object.values(seasons || {});
  return list.some(
    (s) =>
      String(s.start_date_day) !== String(s.orig_start_date_day) ||
      String(s.start_date_month) !== String(s.orig_start_date_month) ||
      String(s.end_date_day) !== String(s.orig_end_date_day) ||
      String(s.end_date_month) !== String(s.orig_end_date_month)
  );
}

// FT-085 REQ-04: единый валидатор пересечений сезонов.
// Семантика дат = Pricing::SeasonCalendar (CON-03): день клампится к концу
// месяца («31.09» → 30.09), месяц вне 1..12 — сезон пропускается,
// end < start = wrap через новый год, границы включительны.
// Февраль клампим к 28: в невисокосный год движок считает «29.02» как 28.02 —
// блокируем худший (реально пересекающийся) случай.
const MONTH_LAST_DAY = [null, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function monthDayOrdinal(day, month) {
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (!Number.isInteger(m) || m < 1 || m > 12) return null;
  if (!Number.isInteger(d) || d < 1 || d > 31) return null;
  return m * 100 + Math.min(d, MONTH_LAST_DAY[m]);
}

function seasonIntervals(season) {
  const start = monthDayOrdinal(season.start_date_day, season.start_date_month);
  const end = monthDayOrdinal(season.end_date_day, season.end_date_month);
  if (start === null || end === null) return null;
  if (end < start) return [[start, 1231], [101, end]];
  return [[start, end]];
}

// Пары индексов конфликтующих сезонов: [{ a, b }, ...] (a < b).
// Непарсибельные сезоны в проверке не участвуют.
export function findSeasonConflicts(seasons) {
  const list = Array.isArray(seasons) ? seasons : Object.values(seasons || {});
  const intervals = list.map(seasonIntervals);
  const conflicts = [];
  for (let a = 0; a < list.length; a++) {
    if (!intervals[a]) continue;
    for (let b = a + 1; b < list.length; b++) {
      if (!intervals[b]) continue;
      const hit = intervals[a].some(([s1, e1]) =>
        intervals[b].some(([s2, e2]) => s1 <= e2 && s2 <= e1)
      );
      if (hit) conflicts.push({ a, b });
    }
  }
  return conflicts;
}

// FT-085 REQ-03: интервалы дня-года для годовой полосы (фиксированный
// 365-дневный календарь, февраль 28 — согласовано с валидатором выше).
// Wrap-сезон возвращает два интервала. Непарсибельный — null.
const MONTH_DOY_OFFSET = [null, 0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

function dayOfYear(ordinal) {
  const m = Math.floor(ordinal / 100);
  const d = ordinal % 100;
  return MONTH_DOY_OFFSET[m] + d;
}

export function seasonDayOfYearIntervals(season) {
  const intervals = seasonIntervals(season);
  if (!intervals) return null;
  return intervals.map(([s, e]) => ({ startDoy: dayOfYear(s), endDoy: dayOfYear(e) }));
}
